import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS } from "../../U3125";
import { u3125FormatInvoiceNumberDisplay, type U3125ForfaitWizardCommitLineResult } from "../../U3125Service";

export type U3125ForfaitWizardCommitResultsProps = {
  results: U3125ForfaitWizardCommitLineResult[];
  /** BL-0271 — afficher le lien PDF seulement si perso enable active. */
  showPdfAction?: boolean;
  onOpenPdf: (invoiceId: string) => void;
  onBackToList: () => void;
  onNewSession: () => void;
};

function resultStatusLabel(status: U3125ForfaitWizardCommitLineResult["status"], t: (key: string, opts?: Record<string, unknown>) => string): string {
  switch (status) {
    case "created":
      return t("wizardResultCreated", { ns: NS });
    case "skipped":
      return t("wizardResultSkipped", { ns: NS });
    case "error":
      return t("wizardResultError", { ns: NS });
    default:
      return status;
  }
}

function formatCommitErrorMessage(
  row: U3125ForfaitWizardCommitLineResult,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (row.errorCode?.startsWith("BU3125")) {
    return t(`err${row.errorCode}`, { ns: NS, defaultValue: row.errorMessage ?? row.errorCode });
  }
  return row.errorMessage ?? "—";
}

const U3125ForfaitWizardCommitResults: React.FC<U3125ForfaitWizardCommitResultsProps> = ({
  results,
  showPdfAction = false,
  onOpenPdf,
  onBackToList,
  onNewSession,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div className="w-full min-w-0 overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblProjectNumber", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("wizardColResult", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblInvoiceStatus", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("lblInvoiceNumber", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("wizardColActions", { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t("wizardColMessage", { ns: NS })}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {results.map((row, idx) => (
              <tr key={`${row.projetNumber ?? row.projectId ?? idx}`} className={row.status === "error" ? "bg-red-50/50" : undefined}>
                <td className="whitespace-nowrap px-3 py-2 font-medium">{row.projetNumber ?? "—"}</td>
                <td className="px-3 py-2">{resultStatusLabel(row.status, t)}</td>
                <td className="px-3 py-2">
                  {row.invoiceStatus ? t(`invoiceStatus_${row.invoiceStatus}`, { ns: NS }) : "—"}
                </td>
                <td className="px-3 py-2">{u3125FormatInvoiceNumberDisplay(row.invoiceNumber)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    {row.invoiceId && (
                      <Link to={`/U3125/${row.invoiceId}`} className="text-blue-600 hover:underline">
                        {t("wizardOpenInvoice", { ns: NS })}
                      </Link>
                    )}
                    {showPdfAction && row.invoiceId && row.invoiceStatus === "RELEASE" && (
                      <button type="button" className="text-blue-600 hover:underline" onClick={() => onOpenPdf(row.invoiceId!)}>
                        {t("wizardDownloadPdf", { ns: NS })}
                      </button>
                    )}
                  </div>
                </td>
                <td className="max-w-md px-3 py-2 text-gray-600">{formatCommitErrorMessage(row, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-2">
        <U2000ActionBtn label={t("wizardBackToList", { ns: NS })} handleClick={onBackToList} />
        <U2000ActionBtn label={t("wizardNewSession", { ns: NS })} handleClick={onNewSession} />
      </div>
    </div>
  );
};

export default U3125ForfaitWizardCommitResults;
